// PPT 관련 Firebase 서비스 함수들
import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  getDoc,
  doc,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import CONFIG from '../config';
import { fetchWithAuth } from '../utils/api';

/**
 * PPT 목록 조회 (사용자별) - 객체 배열 반환
 * @param {string} userId - 사용자 ID (provider_id 형식)
 * @returns {Promise<Object>} PPT 목록 데이터 (pptItems 객체 배열 포함)
 */
export const fetchPptList = async (userId) => {
  try {
    const q = query(
      collection(db, 'PPT_FILE'),
      where('userId', '==', userId),
      where('file_type', '==', 'ppt'),
      orderBy('timestamp', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const pptItems = [];

    querySnapshot.forEach((docSnapshot) => {
      const data = docSnapshot.data();
      pptItems.push({
        docId: docSnapshot.id,
        filename: data.filename || '',
        text: data.text || '',
        totalText: data.total_text || '',
        smallThumbnailUrl: data.small_thumbnail_url || '',
        timestamp: data.timestamp?.toDate?.() || data.timestamp || null,
        totalSlides: data.total_slides || 0,
        fileSize: data.file_size || 0
      });
    });

    return { pptItems };
  } catch (error) {
    console.error('PPT 목록 조회 오류:', error);
    return { pptItems: [] };
  }
};

/**
 * PPT 슬라이드 데이터 조회
 * @param {string} docId - 문서 ID
 * @returns {Promise<Object>} 슬라이드 데이터 (slides 배열에 hasText 필드 포함)
 */
export const fetchPptSlides = async (docId, initialThumbnailCount = CONFIG.INITIAL_THUMBNAIL_COUNT) => {
  try {
    // 1단계: PPT_FILE 문서에서 total_slides와 empty_slides 가져오기
    const pptDocRef = doc(db, 'PPT_FILE', docId);
    const pptDocSnap = await getDoc(pptDocRef);

    let totalSlides = 0;
    let emptySlides = [];
    let slideSize = null;
    let predictionData = null;
    if (pptDocSnap.exists()) {
      const pptData = pptDocSnap.data();
      totalSlides = pptData.total_slides || 0;
      emptySlides = pptData.empty_slides || [];
      predictionData = pptData.prediction_data || null;
      // 슬라이드 크기 (EMU 단위)
      if (pptData.slide_width && pptData.slide_height) {
        slideSize = {
          width: pptData.slide_width,
          height: pptData.slide_height
        };
      }
    }

    // total_slides가 없으면 fallback: 썸네일에서 개수 파악
    if (totalSlides === 0) {
      const thumbnailRef = collection(db, 'PPT_FILE', docId, 'slides_thumbnail');
      const thumbnailQuery = query(thumbnailRef, orderBy('slide_index'));
      const thumbnailSnapshot = await getDocs(thumbnailQuery);

      thumbnailSnapshot.forEach((doc) => {
        const data = doc.data();
        const slideIndex = data.slide_index;
        if (typeof slideIndex === 'number' && !isNaN(slideIndex)) {
          totalSlides = Math.max(totalSlides, slideIndex + 1);
        }
      });
    }

    // 2단계: 초기 썸네일만 조회 (typo_results 조회 제거)
    const thumbnailRef = collection(db, 'PPT_FILE', docId, 'slides_thumbnail');

    // 초기 썸네일 조회 Promise 배열 생성 (병렬 실행)
    const thumbnailPromises = [];
    for (let i = 0; i < Math.min(initialThumbnailCount, totalSlides); i++) {
      thumbnailPromises.push(getDoc(doc(thumbnailRef, String(i))));
    }

    const thumbnailSnapshots = await Promise.all(thumbnailPromises);

    // empty_slides를 Set으로 변환하여 빠른 조회
    const emptySlideSet = new Set(emptySlides);

    // 결과 조합
    const slides = [];
    for (let i = 0; i < totalSlides; i++) {
      let thumbnailUrl = '';
      if (i < initialThumbnailCount && thumbnailSnapshots[i]) {
        const thumbnailSnap = thumbnailSnapshots[i];
        const thumbnail = thumbnailSnap.exists() ? thumbnailSnap.data() : {};
        thumbnailUrl = thumbnail.thumbnail_url || '';
      }

      slides.push({
        slide_index: i,
        thumbnail_url: thumbnailUrl,
        hasText: !emptySlideSet.has(i)  // empty_slides에 없으면 텍스트가 있음
      });
    }

    return { slides, totalSlides, emptySlides, slideSize, predictionData };
  } catch (error) {
    console.error('슬라이드 조회 오류:', error);
    return { slides: [], totalSlides: 0, emptySlides: [], slideSize: null, predictionData: null };
  }
};

/**
 * 특정 범위의 슬라이드 썸네일만 로드 (Lazy Loading)
 * @param {string} docId - 문서 ID
 * @param {number} startIndex - 시작 인덱스
 * @param {number} endIndex - 끝 인덱스 (포함)
 * @returns {Promise<Object>} 썸네일 데이터 { [index]: thumbnail_url }
 */
export const fetchSlideThumbnails = async (docId, startIndex, endIndex) => {
  try {
    const thumbnailRef = collection(db, 'PPT_FILE', docId, 'slides_thumbnail');
    const thumbnails = {};

    // 병렬로 썸네일 조회
    const promises = [];
    for (let i = startIndex; i <= endIndex; i++) {
      promises.push(
        getDoc(doc(thumbnailRef, String(i))).then((snap) => {
          if (snap.exists()) {
            thumbnails[i] = snap.data().thumbnail_url || '';
          }
        })
      );
    }

    await Promise.all(promises);
    return thumbnails;
  } catch (error) {
    console.error('썸네일 조회 오류:', error);
    return {};
  }
};

/**
 * PPT 전체 텍스트 조회
 * @param {string} docId - 문서 ID
 * @returns {Promise<Object>} 전체 텍스트 데이터
 */
export const fetchTotalText = async (docId) => {
  try {
    const docRef = doc(db, 'PPT_FILE', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        total_text: data.total_text || '',
        file_type: data.file_type || '',
        filename: data.filename || '',
        typo_check: data.typo_check || null,
        pdf_url: data.pdf_url || null
      };
    } else {
      return { error: '문서를 찾을 수 없습니다.' };
    }
  } catch (error) {
    console.error('전체 텍스트 조회 오류:', error);
    return { error: error.message };
  }
};

/**
 * PPT 문서의 PDF URL 조회
 * @param {string} docId - 문서 ID
 * @returns {Promise<string|null>} PDF URL
 */
export const fetchPdfUrl = async (docId) => {
  try {
    const docRef = doc(db, 'PPT_FILE', docId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      const data = docSnap.data();
      return data.pdf_url || null;
    }
    return null;
  } catch (error) {
    console.error('PDF URL 조회 오류:', error);
    return null;
  }
};

/**
 * PPT 문서 삭제 (백엔드 API 호출)
 * @param {string} docId - 문서 ID
 * @param {string} userId - 사용자 ID (provider_id 형식, 권한 확인용)
 * @returns {Promise<Object>} 삭제 결과
 */
export const deletePptDocument = async (docId, userId) => {
  try {
    const response = await fetchWithAuth(`${CONFIG.CLOUD_FUNCTION_URL}/delete_ppt_document`, {
      method: 'POST',
      body: JSON.stringify({
        doc_id: docId,
        userId: userId
      })
    });

    const result = await response.json();
    
    if (!response.ok) {
      return { success: false, error: result.error || `HTTP error! status: ${response.status}` };
    }

    return result;
  } catch (error) {
    console.error('PPT 삭제 API 호출 오류:', error);
    return { success: false, error: error.message };
  }
};

/**
 * 업로드 진행상황 실시간 구독 (texts 컬렉션의 upload_progress 필드)
 * @param {string} docId - 문서 ID
 * @param {Function} callback - 진행상황 업데이트 콜백
 * @returns {Function} 구독 해제 함수
 */
export const subscribeToUploadProgress = (docId, callback) => {
  const docRef = doc(db, 'PPT_FILE', docId);

  return onSnapshot(docRef, (doc) => {
    if (doc.exists()) {
      const data = doc.data();
      // upload_progress 필드가 있는 경우에만 콜백 호출
      if (data.upload_progress) {
        callback({
          ...data.upload_progress,
          small_thumbnail_url: data.small_thumbnail_url || null,
          pdf_url: data.pdf_url || null,
          total_slides: data.total_slides || 0
        });
      } else {
        // upload_progress 필드가 없으면 기본값 제공
        callback({
          status: '대기 중...',
          completed: false,
          small_thumbnail_url: null,
          pdf_url: null,
          total_slides: 0
        });
      }
    } else {
      // 문서가 아직 생성되지 않은 경우
      callback({
        status: '초기화 중...',
        completed: false,
        small_thumbnail_url: null,
        pdf_url: null,
        total_slides: 0
      });
    }
  }, (error) => {
    console.error('진행상황 구독 오류:', error);
    callback({ error: error.message });
  });
};
